export interface InsightData {
  orderStatus: { label: string; count: number; color: string }[];
  drivers: { active: number; busy: number; offline: number };
  topService: { name: string; orders: number; pct: number }[];
}

interface InsightCardsProps {
  data: InsightData;
  delay?: number;
}

export default function InsightCards({ data, delay = 0 }: InsightCardsProps) {
  const total = data.orderStatus.reduce((s, i) => s + i.count, 0) || 1;
  const totalDrivers = data.drivers.active + data.drivers.busy + data.drivers.offline || 1;

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      {/* ── Card 1: Order status breakdown ── */}
      <div
        className="animate-fade-up rounded-card border border-background-200 bg-white p-5 transition-all duration-300 hover:-translate-y-0.5 hover:border-background-300 hover:shadow-soft"
        style={{ animationDelay: `${delay}ms` }}
      >
        <div className="mb-4 flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-secondary-100 text-secondary-600">
            <i className="ri-pie-chart-2-line text-[17px]" />
          </span>
          <h3 className="font-semibold text-[13px] text-foreground-800">توزيع الطلبات</h3>
        </div>

        {/* Segmented bar */}
        <div className="mb-4 flex h-2.5 w-full overflow-hidden rounded-full bg-background-100">
          {data.orderStatus.map((item) => (
            <div
              key={item.label}
              className={`h-full ${item.color} transition-all duration-700`}
              style={{ width: `${(item.count / total) * 100}%` }}
            />
          ))}
        </div>

        <div className="space-y-2.5">
          {data.orderStatus.map((item) => (
            <div key={item.label} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${item.color}`} />
                <span className="text-[12px] text-foreground-600">{item.label}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-1 w-16 overflow-hidden rounded-full bg-background-100">
                  <div
                    className={`h-full ${item.color} opacity-70`}
                    style={{ width: `${(item.count / total) * 100}%` }}
                  />
                </div>
                <span className="w-5 text-start font-semibold text-[12px] text-foreground-900">{item.count}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Card 2: Driver availability ── */}
      <div
        className="animate-fade-up rounded-card border border-background-200 bg-white p-5 transition-all duration-300 hover:-translate-y-0.5 hover:border-background-300 hover:shadow-soft"
        style={{ animationDelay: `${delay + 75}ms` }}
      >
        <div className="mb-4 flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
            <i className="ri-motorbike-line text-[17px]" />
          </span>
          <h3 className="font-semibold text-[13px] text-foreground-800">المناديب الآن</h3>
        </div>

        {/* Donut-style visual */}
        <div className="mb-4 flex items-center justify-center gap-3">
          <div className="relative flex h-24 w-24 items-center justify-center">
            <svg viewBox="0 0 36 36" className="h-24 w-24 -rotate-90">
              {/* Background circle */}
              <circle cx="18" cy="18" r="14" fill="none" stroke="oklch(var(--background-100))" strokeWidth="4" />
              {/* Active */}
              <circle
                cx="18" cy="18" r="14"
                fill="none"
                stroke="oklch(var(--success-500))"
                strokeWidth="4"
                strokeDasharray={`${(data.drivers.active / totalDrivers) * 88} 88`}
                strokeLinecap="round"
              />
              {/* Busy - offset */}
              <circle
                cx="18" cy="18" r="14"
                fill="none"
                stroke="oklch(var(--secondary-400))"
                strokeWidth="4"
                strokeDasharray={`${(data.drivers.busy / totalDrivers) * 88} 88`}
                strokeDashoffset={`${-((data.drivers.active / totalDrivers) * 88)}`}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="font-bold text-[20px] leading-none text-foreground-950">{totalDrivers}</span>
              <span className="text-[10px] text-foreground-400">مندوب</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="rounded-lg bg-success-50 px-2 py-2.5">
            <p className="font-bold text-[18px] text-success-600">{data.drivers.active}</p>
            <p className="mt-0.5 text-[10px] text-foreground-500">متاح</p>
          </div>
          <div className="rounded-lg bg-secondary-50 px-2 py-2.5">
            <p className="font-bold text-[18px] text-secondary-600">{data.drivers.busy}</p>
            <p className="mt-0.5 text-[10px] text-foreground-500">مشغول</p>
          </div>
          <div className="rounded-lg bg-background-100 px-2 py-2.5">
            <p className="font-bold text-[18px] text-foreground-500">{data.drivers.offline}</p>
            <p className="mt-0.5 text-[10px] text-foreground-500">غير متاح</p>
          </div>
        </div>
      </div>

      {/* ── Card 3: Top services ── */}
      <div
        className="animate-fade-up rounded-card border border-background-200 bg-white p-5 transition-all duration-300 hover:-translate-y-0.5 hover:border-background-300 hover:shadow-soft"
        style={{ animationDelay: `${delay + 150}ms` }}
      >
        <div className="mb-4 flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
            <i className="ri-trophy-line text-[17px]" />
          </span>
          <h3 className="font-semibold text-[13px] text-foreground-800">أكثر الخدمات طلباً</h3>
        </div>

        <div className="space-y-3">
          {data.topService.map((svc, i) => (
            <div key={svc.name}>
              <div className="mb-1 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-100 text-[10px] font-bold text-primary-700">
                    {i + 1}
                  </span>
                  <span className="text-[12px] text-foreground-700">{svc.name}</span>
                </div>
                <span className="text-[12px] font-semibold text-foreground-900">{svc.orders} طلب</span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-background-100">
                <div
                  className={`h-full rounded-full transition-all duration-700 ${
                    i === 0 ? 'bg-primary-500' : i === 1 ? 'bg-secondary-400' : 'bg-accent-400'
                  }`}
                  style={{ width: `${svc.pct}%` }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Footer note */}
        <div className="mt-4 flex items-center gap-1.5 rounded-lg bg-background-50 px-3 py-2">
          <i className="ri-calendar-line text-[13px] text-foreground-400" />
          <span className="text-[11px] text-foreground-500">بيانات اليوم فقط — {new Date().toLocaleDateString('ar-SA', { weekday: 'long' })}</span>
        </div>
      </div>
    </div>
  );
}